import moment from 'moment'

import {
  AccountingTz,
  getTzAbbr,
  resolveAccountingTimezone,
} from '~/utils/timezone'

type ZonedTimeFormat = 'h:mm A' | 'DD MMM YYYY [at] h:mm A'
const zonedFormatters = new Map<string, Intl.DateTimeFormat>()

const formatInZone = (
  date: Date,
  tz: AccountingTz,
  format?: ZonedTimeFormat,
) => {
  const zone = resolveAccountingTimezone(tz)
  let formatter = zonedFormatters.get(zone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    zonedFormatters.set(zone, formatter)
  }
  const parts = formatter.formatToParts(date)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const time = `${part('hour')}:${part('minute')} ${part('dayPeriod')}`
  const abbreviation = getTzAbbr(zone, date)
  if (format === 'h:mm A') return `${time} ${abbreviation}`
  if (format === 'DD MMM YYYY [at] h:mm A')
    return `${part('day')} ${part('month')} ${part('year')} at ${time} ${abbreviation}`
  return `${part('month')} ${part('day')} ${part('year')} ${time} ${abbreviation}`
}

export function formatWithLocalTimezone(
  date: Date,
  options: {
    format?: ZonedTimeFormat
    tz?: AccountingTz
  } = {},
) {
  const { format, tz = 'local' } = options
  if (tz !== 'utc' && tz !== 'local') {
    return formatInZone(date, tz, format)
  }
  const timeZoneName = getTzAbbr(tz, date)
  const m = tz === 'utc' ? moment(date).utc() : moment(date).local()

  return m.format(`${format ?? 'MMM Do YYYY HH:mm:ss'} [${timeZoneName}]`)
}

export const ONE_WEEK_MS = 1000 * 60 * 60 * 24 * 7
export const getTimeDiffMs = (timestamp: number | string) => {
  return new Date().getTime() - new Date(timestamp).getTime()
}

export const calendarDaysBetween = (from: Date, to: Date) => {
  const calendarDay = (date: Date) =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())

  return (calendarDay(to) - calendarDay(from)) / (1000 * 60 * 60 * 24)
}

export const dayOfMonthLabel = (date: Date) => {
  const day = date.getDate()
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th'
  return `the ${day}${suffix}`
}
